import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Loader2, AlertTriangle, Coffee, Pencil } from "lucide-react";

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"; } catch { return "—"; } };
const EX_LABEL = {
  late_start: { t: "Late start", c: "bg-amber-50 text-amber-600" },
  early_finish: { t: "Early finish", c: "bg-sky-50 text-sky-600" },
  overtime: { t: "Overtime", c: "bg-indigo-50 text-indigo-600" },
  unscheduled: { t: "Unscheduled", c: "bg-slate-100 text-slate-500" },
  missing_clock_out: { t: "Needs review", c: "bg-rose-50 text-rose-600" },
  corrected: { t: "Corrected", c: "bg-emerald-50 text-emerald-600" },
  location_review_pending: { t: "Location review pending", c: "bg-amber-50 text-amber-700" },
  location_rejected: { t: "Location exception rejected", c: "bg-rose-50 text-rose-600" },
};

export const ActiveShiftsPanel = ({ sites = [] }) => {
  const [rows, setRows] = useState(null);
  const [siteId, setSiteId] = useState("");
  const load = useCallback(() => {
    api.get(`/workforce/attendance/active${siteId ? `?site_id=${siteId}` : ""}`).then((r) => setRows(r.data.active)).catch(() => setRows([]));
  }, [siteId]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  if (!rows) return <div className="p-6 text-slate-400" data-testid="active-loading"><Loader2 className="animate-spin inline" /> Loading…</div>;
  return (
    <div data-testid="active-shifts-panel">
      <div className="flex items-center gap-2 mb-3">
        <select className="border rounded-lg p-2 text-sm" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="active-site-filter">
          <option value="">All my sites</option>
          {sites.map((s) => <option key={s.venue_id} value={s.venue_id}>{s.name}</option>)}
        </select>
        <span className="text-sm text-slate-500">{rows.length} currently working</span>
      </div>
      {rows.length === 0 ? <p className="text-slate-400 text-sm" data-testid="active-empty">Nobody is clocked in right now.</p> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.attendance_id} className="bg-white border rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`active-${r.attendance_id}`}>
              <div>
                <div className="font-semibold text-slate-800">{r.person_name}</div>
                <div className="text-xs text-slate-500">{r.site_name} · since {fmt(r.started_at_utc)} · {Math.floor(r.elapsed_minutes / 60)}h {r.elapsed_minutes % 60}m</div>
                {r.scheduled_start && <div className="text-xs text-slate-400">Scheduled {r.scheduled_start}–{r.scheduled_end}</div>}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                {r.active_break && <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Coffee size={11} /> On break</span>}
                {r.unscheduled && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Unscheduled</span>}
                {r.status === "NEEDS_REVIEW" && <span className="text-xs bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle size={11} /> Needs review</span>}
                {r.start_variance_minutes > 5 && <span className="text-xs text-amber-600">{r.start_variance_minutes}m late</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CorrectionModal = ({ row, onClose, onSaved }) => {
  const [reason, setReason] = useState("");
  const [start, setStart] = useState(row.started_at_utc ? row.started_at_utc.slice(0, 16) : "");
  const [end, setEnd] = useState(row.ended_at_utc ? row.ended_at_utc.slice(0, 16) : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    const body = { correction_reason: reason };
    if (start) body.started_at_utc = new Date(start).toISOString();
    if (end) body.ended_at_utc = new Date(end).toISOString();
    try { await api.patch(`/workforce/attendance/sessions/${row.attendance_id}`, body); toast.success("Attendance corrected"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save correction"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="correction-modal">
        <DialogHeader><DialogTitle>Correct attendance — {row.person_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Start (local)</Label><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="correct-start" /></div>
          <div><Label>End (local)</Label><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="correct-end" /></div>
          <div><Label>Reason <span className="text-rose-500">*</span></Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being corrected?" data-testid="correct-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="correct-save">{busy ? <Loader2 size={16} className="animate-spin" /> : "Save correction"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TimesheetsPanel = ({ sites = [] }) => {
  const [rows, setRows] = useState(null);
  const [nrCount, setNrCount] = useState(0);
  const [summary, setSummary] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    const p = {};
    if (siteId) p.site_id = siteId;
    if (from) p.from_date = from;
    if (to) p.to_date = to;
    if (needsOnly) p.needs_review = true;
    api.get("/workforce/attendance/timesheets", { params: p }).then((r) => { setRows(r.data.rows); setNrCount(r.data.needs_review_count || 0); }).catch(() => setRows([]));
    api.get("/workforce/attendance/timesheets/summary", { params: { site_id: p.site_id, from_date: p.from_date, to_date: p.to_date } }).then((r) => setSummary(r.data.people || [])).catch(() => setSummary([]));
  }, [siteId, from, to, needsOnly]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (siteId) qs.set("site_id", siteId);
    if (from) qs.set("from_date", from);
    if (to) qs.set("to_date", to);
    // Auth via the httponly session cookie (same-site top-level GET), mirroring the rota CSV export.
    // Never place the bearer token in the URL — it would leak into proxy/server access logs.
    window.open(`${api.defaults.baseURL}/workforce/attendance/timesheets/export.csv?${qs.toString()}`, "_blank");
  };
  return (
    <div data-testid="timesheets-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select className="border rounded-lg p-2 text-sm" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="ts-site-filter">
          <option value="">All my sites</option>
          {sites.map((s) => <option key={s.venue_id} value={s.venue_id}>{s.name}</option>)}
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="ts-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="ts-to" />
        <Button variant={needsOnly ? "default" : "outline"} onClick={() => setNeedsOnly((v) => !v)} data-testid="ts-needs-review-filter" className={needsOnly ? "bg-rose-600 hover:bg-rose-700" : ""}>
          <AlertTriangle size={15} className="mr-1" /> Needs review
          {nrCount > 0 && <span className="ml-1.5 text-xs bg-rose-100 text-rose-700 rounded-full px-1.5" data-testid="ts-needs-review-badge">{nrCount}</span>}
        </Button>
        <Button variant="outline" onClick={() => setShowSummary((v) => !v)} data-testid="ts-summary-toggle">{showSummary ? "Hide" : "Show"} weekly summary</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="ts-export"><Download size={15} className="mr-1" /> Export CSV</Button>
      </div>

      {showSummary && (
        <div className="mb-4 overflow-x-auto" data-testid="ts-summary">
          <div className="text-xs font-semibold text-slate-500 mb-1">Worked vs scheduled (this range) — descriptive only, not a score</div>
          {summary.length === 0 ? <p className="text-slate-400 text-sm" data-testid="ts-summary-empty">Nothing to summarise.</p> : (
            <table className="w-full text-sm" data-testid="ts-summary-table">
              <thead><tr className="text-left text-slate-500 border-b">
                <th className="p-2">Person</th><th className="p-2">Scheduled</th><th className="p-2">Actual</th>
                <th className="p-2">Variance</th><th className="p-2">Breaks (p/u)</th><th className="p-2">Shifts</th><th className="p-2">Unscheduled</th>
              </tr></thead>
              <tbody>
                {summary.map((p) => (
                  <tr key={p.person_id} className="border-b" data-testid={`ts-summary-${p.person_id}`}>
                    <td className="p-2 font-medium">{p.person_name}</td>
                    <td className="p-2 text-slate-500">{p.scheduled_hours}h</td>
                    <td className="p-2">{p.actual_hours}h</td>
                    <td className="p-2 text-slate-600">{p.variance_hours > 0 ? "+" : ""}{p.variance_hours}h</td>
                    <td className="p-2 text-slate-500">{p.paid_break_minutes}p / {p.unpaid_break_minutes}u</td>
                    <td className="p-2 text-slate-500">{p.sessions}</td>
                    <td className="p-2 text-slate-500">{p.unscheduled_sessions > 0 ? <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{p.unscheduled_sessions}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!rows ? <div className="p-6 text-slate-400"><Loader2 className="animate-spin inline" /> Loading…</div> : rows.length === 0 ? (
        <p className="text-slate-400 text-sm" data-testid="ts-empty">{needsOnly ? "No shifts need review in this range." : "No attendance in this range."}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ts-table">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="p-2">Date</th><th className="p-2">Person</th><th className="p-2">Site</th>
              <th className="p-2">Scheduled</th><th className="p-2">Actual</th><th className="p-2">Hours</th>
              <th className="p-2">Breaks</th><th className="p-2">Flags</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.attendance_id} className="border-b" data-testid={`ts-row-${r.attendance_id}`}>
                  <td className="p-2">{r.business_date}</td>
                  <td className="p-2 font-medium">{r.person_name}</td>
                  <td className="p-2 text-slate-500">{r.site_name}</td>
                  <td className="p-2 text-slate-500">{r.scheduled_start ? `${r.scheduled_start}–${r.scheduled_end}` : "—"}</td>
                  <td className="p-2">{fmt(r.started_at_utc)}–{r.ended_at_utc ? fmt(r.ended_at_utc) : "…"}</td>
                  <td className="p-2">{r.actual_hours}h</td>
                  <td className="p-2 text-slate-500">{r.paid_break_minutes}p / {r.unpaid_break_minutes}u</td>
                  <td className="p-2"><div className="flex gap-1 flex-wrap">{(r.exceptions || []).map((ex) => EX_LABEL[ex] && <span key={ex} className={`text-xs px-1.5 py-0.5 rounded ${EX_LABEL[ex].c}`}>{EX_LABEL[ex].t}</span>)}</div></td>
                  <td className="p-2"><Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`ts-correct-${r.attendance_id}`}><Pencil size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <CorrectionModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
